"use client";

import { useEffect, useRef } from "react";
import { useCopilotChat } from "@zapier/copilot-ui/client";
import { COPILOT_CONVERSATION_PROXY_ROUTE } from "@/utils/constants";

type ConversationSaveState = {
  id: string;
  /**
   * User message count when history finished loading.
   * null while history is still in-flight.
   * Messages at or below this count came from history, not this session.
   */
  baseline: number | null;
  /** Count at which we last PUT; prevents duplicate requests. */
  lastPutCount: number;
};

function putConversationId(conversationId: string, copilotId?: string): void {
  const body: Record<string, string> = {
    copilot_conversation_id: conversationId,
  };
  if (copilotId) body.copilot_id = copilotId;
  fetch(COPILOT_CONVERSATION_PROXY_ROUTE, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).catch(() => {
    // Silently ignore — conversation persistence is best-effort.
  });
}

/**
 * Saves the current copilot conversationId to the backend whenever the user
 * sends a new message in this session. Must be called inside a CopilotProvider.
 *
 * History-aware: waits for `isLoadingMessages` to settle before capturing a
 * baseline message count. Only PUTs when the live count exceeds that baseline,
 * preventing spurious writes when history loads on page visit.
 */
export function useSaveCopilotConversationId(copilotId?: string): void {
  const { conversationId, messages, isLoadingMessages } = useCopilotChat();
  const userMessageCount = messages.filter((m) => m.role === "user").length;
  const stateRef = useRef<ConversationSaveState | null>(null);

  useEffect(() => {
    if (!conversationId) return;

    const state = stateRef.current;

    // New or reset conversation — reinitialise tracking state.
    if (!state || state.id !== conversationId) {
      stateRef.current = {
        id: conversationId,
        baseline: isLoadingMessages ? null : userMessageCount,
        lastPutCount: 0,
      };
      return;
    }

    // History still loading — defer baseline capture until it settles.
    if (isLoadingMessages) return;

    // History just settled — lock in the baseline.
    if (state.baseline === null) {
      stateRef.current = { ...state, baseline: userMessageCount };
      return;
    }

    // PUT when the user has sent new messages beyond the history baseline.
    if (
      userMessageCount <= state.baseline ||
      state.lastPutCount >= userMessageCount
    )
      return;

    stateRef.current = { ...state, lastPutCount: userMessageCount };
    putConversationId(conversationId, copilotId);
  }, [conversationId, userMessageCount, isLoadingMessages]);
}
